import type { ReactNode } from "react";
import { Fragment } from "react";

/**
 * A small, purpose-built renderer for the two legal drafts
 * (src/content/legal/*.md, copied verbatim from docs/13 and docs/14).
 *
 * This is intentionally not a general-purpose Markdown library: the two
 * source documents use a narrow, consistent subset of Markdown (h1/h2/h3
 * headings, a leading blockquote status note, a "**Last updated:**" line,
 * bullet lists, paragraphs, inline bold and one inline code span). Handling
 * exactly that subset here avoids pulling in a new runtime dependency while
 * guaranteeing the legal text is reproduced verbatim — this file never
 * rewrites, summarizes or drops any sentence from the source.
 */

export interface LegalHeading {
  id: string;
  text: string;
}

export interface ParsedLegalDocument {
  /** The document's literal H1 text, e.g. "DataProtection.work Privacy Policy — Draft". */
  title: string;
  /** The literal blockquote status note directly under the H1, if present. */
  statusNote: string | null;
  /** The literal "**Last updated:** ..." value, if present. */
  lastUpdated: string | null;
  /** Top-level (##) headings, for an in-page contents list. */
  headings: LegalHeading[];
  /** Fully rendered body, starting at the first ## heading. */
  body: ReactNode;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/['".]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Renders **bold** and `code` inline spans; everything else is plain text. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Split on **bold** and `code` while keeping the delimiters, so we can
  // classify each piece by which delimiter (if any) wraps it.
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter((p) => p !== "");

  parts.forEach((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith("**") && part.endsWith("**")) {
      nodes.push(<strong key={key}>{part.slice(2, -2)}</strong>);
    } else if (part.startsWith("`") && part.endsWith("`")) {
      nodes.push(
        <code key={key} className="rounded bg-secondary px-1.5 py-0.5 text-[0.9em]">
          {part.slice(1, -1)}
        </code>,
      );
    } else {
      nodes.push(<Fragment key={key}>{part}</Fragment>);
    }
  });

  return nodes;
}

/** Renders a paragraph block's raw lines, honoring markdown hard breaks (trailing "  "). */
function renderParagraphLines(lines: string[], keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  lines.forEach((line, i) => {
    const hardBreak = /[ ]{2,}$/.test(line);
    nodes.push(...renderInline(line.replace(/[ ]{2,}$/, ""), `${keyPrefix}-l${i}`));
    if (hardBreak && i < lines.length - 1) {
      nodes.push(<br key={`${keyPrefix}-br${i}`} />);
    } else if (!hardBreak && i < lines.length - 1) {
      nodes.push(" ");
    }
  });
  return nodes;
}

interface Block {
  type: "h2" | "h3" | "ul" | "p";
  lines: string[];
}

function splitBlocks(lines: string[]): Block[] {
  const blocks: Block[] = [];
  let current: string[] = [];
  let currentType: Block["type"] | null = null;

  function flush() {
    if (current.length > 0 && currentType) {
      blocks.push({ type: currentType, lines: current });
    }
    current = [];
    currentType = null;
  }

  for (const rawLine of lines) {
    const line = rawLine;
    if (line.trim() === "") {
      flush();
      continue;
    }

    let type: Block["type"];
    if (line.startsWith("### ")) type = "h3";
    else if (line.startsWith("## ")) type = "h2";
    else if (line.startsWith("- ")) type = "ul";
    else type = "p";

    if (type !== currentType || type === "h2" || type === "h3") {
      flush();
      currentType = type;
    }
    current.push(line);
  }
  flush();

  return blocks;
}

/**
 * Substrings identifying paragraphs that describe unresolved legal
 * placeholders (an undesignated entity/address, an unfinalized liability
 * cap or governing-law clause). Any paragraph containing one of these is
 * rendered in a distinct "to be finalized" callout instead of as plain
 * body text — the sentence itself is reproduced verbatim either way.
 */
export function parseLegalDocument(source: string, placeholderMarkers: string[]): ParsedLegalDocument {
  const lines = source.replace(/\r\n/g, "\n").split("\n");

  let i = 0;
  // 1. Title (H1)
  let title = "";
  const firstLine = lines[i];
  if (firstLine?.startsWith("# ")) {
    title = firstLine.slice(2).trim();
    i++;
  }
  while ((lines[i] ?? "").trim() === "") i++;

  // 2. Status blockquote
  let statusNote: string | null = null;
  let quoteLine = lines[i];
  if (quoteLine?.startsWith(">")) {
    const quoteLines: string[] = [];
    while (quoteLine?.startsWith(">")) {
      quoteLines.push(quoteLine.replace(/^>\s?/, ""));
      i++;
      quoteLine = lines[i];
    }
    statusNote = quoteLines.join(" ").replace(/\*\*/g, "");
  }
  while ((lines[i] ?? "").trim() === "") i++;

  // 3. "**Last updated:** ..." line
  let lastUpdated: string | null = null;
  const lastUpdatedMatch = lines[i]?.match(/^\*\*Last updated:\*\*\s*(.+)$/);
  if (lastUpdatedMatch) {
    lastUpdated = (lastUpdatedMatch[1] ?? "").trim();
    i++;
  }

  // 4. Body: everything from here to the end, block-parsed.
  const bodyLines = lines.slice(i);
  const blocks = splitBlocks(bodyLines);

  const headings: LegalHeading[] = [];
  const bodyNodes: ReactNode[] = [];

  blocks.forEach((block, blockIndex) => {
    const key = `block-${blockIndex}`;

    if (block.type === "h2") {
      const text = (block.lines[0] ?? "").replace(/^## /, "").trim();
      const id = slugify(text);
      headings.push({ id, text });
      bodyNodes.push(
        <h2 key={key} id={id} className="mt-12 scroll-mt-24 text-2xl font-bold tracking-tight text-foreground first:mt-0 sm:text-[1.7rem]">
          {renderInline(text, key)}
        </h2>,
      );
      return;
    }

    if (block.type === "h3") {
      const text = (block.lines[0] ?? "").replace(/^### /, "").trim();
      const id = slugify(text);
      bodyNodes.push(
        <h3 key={key} id={id} className="mt-8 scroll-mt-24 text-lg font-semibold text-foreground">
          {renderInline(text, key)}
        </h3>,
      );
      return;
    }

    if (block.type === "ul") {
      bodyNodes.push(
        <ul key={key} className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed text-foreground">
          {block.lines.map((line, li) => (
            <li key={`${key}-${li}`}>{renderInline(line.replace(/^- /, ""), `${key}-${li}`)}</li>
          ))}
        </ul>,
      );
      return;
    }

    // Paragraph
    const rawText = block.lines.join(" ");
    const isPlaceholder = placeholderMarkers.some((marker) => rawText.includes(marker));
    const paragraph = (
      <p key={key} className="mt-4 text-base leading-relaxed text-foreground">
        {renderParagraphLines(block.lines, key)}
      </p>
    );

    if (isPlaceholder) {
      bodyNodes.push(
        <div
          key={`${key}-wrap`}
          role="note"
          aria-label="Unresolved placeholder, to be finalized before public launch"
          className="mt-4 rounded-lg border border-dashed border-warning/50 bg-warning/10 px-4 py-3"
        >
          <p className="text-xs font-bold uppercase tracking-wide text-warning">
            To be finalized before public launch
          </p>
          <p className="mt-1.5 text-base leading-relaxed text-foreground">
            {renderParagraphLines(block.lines, key)}
          </p>
        </div>,
      );
    } else {
      bodyNodes.push(paragraph);
    }
  });

  return {
    title,
    statusNote,
    lastUpdated,
    headings,
    body: bodyNodes,
  };
}
